import React, { useState, useRef, useEffect } from "react";
import { t as T } from "i18next";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon, Film, X, Globe, Users, Heart, MessageSquareOff, MessageSquare, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PromoModal from "./PromoModal";
import { uploadPostFile, postUploadLimit } from "../../lib/postUpload";

const VIS_OPTIONS = [
  { v: "public", icon: Globe, labelKey: "post.vis_public", fallback: "Alle" },
  { v: "followers", icon: Users, labelKey: "post.vis_followers", fallback: "Følgere" },
  { v: "friends", icon: Heart, labelKey: "post.vis_friends", fallback: "Venner" },
];

export default function PostComposer({ onPosted, editPost = null, onCancel = null, pool = "private" }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isEdit = !!editPost;
  const [text, setText] = useState(editPost?.text || "");
  const [visibility, setVisibility] = useState(editPost?.visibility || "public");
  const [commentsDisabled, setCommentsDisabled] = useState(!!editPost?.comments_disabled);
  const [optOutContest, setOptOutContest] = useState(false);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [promo, setPromo] = useState(editPost?.promo || null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const fileRef = useRef();
  const textRef = useRef();

  // @-autocomplete-state
  const [mentionSuggest, setMentionSuggest] = useState([]);
  const [mentionActive, setMentionActive] = useState(false);
  const [mentionQ, setMentionQ] = useState("");
  const [mentionIdx, setMentionIdx] = useState(0);
  const [cursorPos, setCursorPos] = useState(0);

  // Fetch forslag med debounce
  useEffect(() => {
    if (!mentionActive || !mentionQ) {
      setMentionSuggest([]);
      return;
    }
    const timer = setTimeout(() => {
      api.get("/users/suggest", { params: { q: mentionQ, limit: 6 } })
        .then((r) => setMentionSuggest(r.data?.users || []))
        .catch(() => setMentionSuggest([]));
    }, 180);
    return () => clearTimeout(timer);
  }, [mentionQ, mentionActive]);

  const handleTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    const pos = e.target.selectionStart || 0;
    setCursorPos(pos);
    // Find @-trigger: siste @ før cursor + ingen whitespace mellom
    const before = v.slice(0, pos);
    const atMatch = before.match(/(?:^|\s)@([a-zA-Z0-9_]{1,30})$/);
    if (atMatch) {
      setMentionActive(true);
      setMentionQ(atMatch[1]);
      setMentionIdx(0);
    } else {
      setMentionActive(false);
      setMentionQ("");
    }
  };

  const insertMention = (uname) => {
    const before = text.slice(0, cursorPos);
    const after = text.slice(cursorPos);
    // Erstatt siste @partial
    const replaced = before.replace(/(?:^|\s)@([a-zA-Z0-9_]{1,30})$/, (match, g1) => {
      return match.slice(0, match.length - g1.length) + uname + " ";
    });
    const newText = replaced + after;
    setText(newText);
    setMentionActive(false);
    setMentionSuggest([]);
    setMentionQ("");
    setTimeout(() => {
      if (textRef.current) {
        const pos = replaced.length;
        textRef.current.focus();
        textRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (mentionActive && mentionSuggest.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIdx((i) => (i + 1) % mentionSuggest.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIdx((i) => (i - 1 + mentionSuggest.length) % mentionSuggest.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(mentionSuggest[mentionIdx].username);
      } else if (e.key === "Escape") {
        setMentionActive(false);
      }
    }
  };

  const addFiles = (newList) => {
    if (isEdit) return;
    const list = Array.from(newList || []);
    const hasVideo = list.some((f) => f.type.startsWith("video/")) || files.some((f) => f.type.startsWith("video/"));
    if (hasVideo && (list.length + files.length) > 1) {
      toast.error(T("ui.media_mix_error"));
      return;
    }
    const combined = [...files, ...list].slice(0, 4);
    const limit = postUploadLimit(user);
    if (combined.some((f) => f.size > limit)) {
      toast.error(`Maks ${Math.round(limit / (1024 * 1024))} MB per fil.`);
      return;
    }
    setFiles(combined);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!text.trim() && files.length === 0 && !isEdit && !promo) {
      toast.error(t("post.empty_err", "Posten må ha tekst, media eller tilbud."));
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/profile-posts/${editPost.id}`, {
          text, visibility, comments_disabled: commentsDisabled,
        });
        toast.success(t("post.updated", "Post oppdatert."));
        onPosted?.({ text: text || null, visibility, comments_disabled: commentsDisabled, edited: true });
      } else {
        const fd = new FormData();
        fd.append("text", text);
        fd.append("visibility", visibility);
        fd.append("comments_disabled", commentsDisabled ? "true" : "false");
        fd.append("owner_pool", pool);
        fd.append("opt_out_contest", optOutContest ? "true" : "false");
        const wmOn = ["corner", "diagonal"].includes(user?.watermark_default);
        const allMedia = [...files, ...(promo?.mediaFiles || [])];
        if (wmOn && allMedia.some((f) => f.type.startsWith("video/") && f.size > 1024 * 1024 * 1024)) {
          toast.info(T("ui.video_wm_1gb"));
        }
        if (promo) {
          fd.append("promo_kind", promo.kind);
          fd.append("promo_title", promo.title || "");
          fd.append("promo_body", promo.body || "");
          if (promo.link) fd.append("promo_link", promo.link);
          if (promo.kind === "percent" && promo.discount_pct)
            fd.append("promo_discount_pct", String(promo.discount_pct));
          if (promo.expires_at) fd.append("promo_expires_at", promo.expires_at);
        }
        const all = [
          ...files.map((f) => [f, "post"]),
          ...(promo?.mediaFiles || []).map((f) => [f, "promo"]),
          ...(promo?.qrFile ? [[promo.qrFile, "qr"]] : []),
        ];
        const ids = { post: [], promo: [], qr: [] };
        for (let i = 0; i < all.length; i++) {
          const [f, purpose] = all[i];
          ids[purpose].push(await uploadPostFile(f, purpose, (p) =>
            setProgress(`Laster opp ${i + 1}/${all.length} (${Math.round(p * 100)} %)`)));
        }
        if (all.length) setProgress("Sikkerhetssjekker…");
        fd.append("upload_ids", ids.post.join(","));
        fd.append("promo_upload_ids", ids.promo.join(","));
        if (ids.qr[0]) fd.append("promo_qr_upload_id", ids.qr[0]);
        try {
          await api.post("/profile-posts", fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (pe) {
          const d = pe?.response?.data?.detail;
          if (d?.code !== "watermark_too_large" || !window.confirm(`${d.message}\n\nLagre uten innbrent vannmerke?`)) throw pe;
          fd.set("skip_watermark", "true");
          await api.post("/profile-posts", fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success(t("post.posted", "Post publisert."));
        setText(""); setFiles([]); setVisibility("public"); setCommentsDisabled(false); setPromo(null); setOptOutContest(false);
        onPosted?.();
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || e?.message || T("ui.publish_failed"));
    } finally {
      setProgress("");
      setBusy(false);
    }
  };

  const curVis = VIS_OPTIONS.find((v) => v.v === visibility);
  const Icon = curVis.icon;

  return (
    <div className="panel-tight p-4 mb-4 relative" data-testid={isEdit ? `post-editor-${editPost.id}` : "post-composer"}>
      {isEdit && (
        <div className="font-meta text-[10px] uppercase tracking-wider text-[var(--magenta)] mb-2">
          {t("post.editing", "Redigerer post")}
        </div>
      )}
      <textarea
        ref={textRef}
        value={text}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setMentionActive(false), 150)}
        maxLength={2000}
        rows={3}
        placeholder={t("post.placeholder", "Hva tenker du?")}
        className="input-editorial w-full font-manrope text-sm resize-none"
        data-testid="post-text"
      />

      {/* Autocomplete-dropdown */}
      {mentionActive && mentionSuggest.length > 0 && (
        <div
          className="absolute left-4 right-4 top-28 z-30 bg-[#1a1618] border border-white/10 rounded-md shadow-xl max-h-64 overflow-y-auto"
          data-testid="mention-suggest"
        >
          {mentionSuggest.map((u, i) => (
            <button
              key={u.id}
              onMouseDown={(e) => { e.preventDefault(); insertMention(u.username); }}
              className={`w-full text-left flex items-center gap-2 px-3 py-2 ${
                i === mentionIdx ? "bg-[var(--magenta)]/20" : "hover:bg-white/5"
              }`}
              data-testid={`mention-opt-${u.username}`}
            >
              {u.avatar_url ? (
                <img src={u.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-[var(--magenta)]/30 flex items-center justify-center text-[10px] font-bold text-white">
                  {(u.username || "?").slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[var(--cream)] truncate font-fraunces italic">
                  {u.display_name || u.username}
                </div>
                <div className="font-meta text-[10px] text-[var(--cream-faint)] truncate">@{u.username}</div>
              </div>
              {u.is_creator && (
                <span className="font-meta text-[9px] text-[var(--gold)] uppercase tracking-wider shrink-0">{T("ui.creator_badge")}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {!isEdit && files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={i} className="relative w-24 h-24 bg-black/40 rounded overflow-hidden">
              {f.type.startsWith("video/") ? (
                <div className="flex items-center justify-center h-full text-xs text-[var(--cream-soft)]">
                  <Film className="h-8 w-8" />
                </div>
              ) : (
                <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
              )}
              <button onClick={() => removeFile(i)} className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5">
                <X className="h-3 w-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {!isEdit && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={(e) => addFiles(e.target.files)}
                className="hidden"
                data-testid="post-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                className="btn-ghost text-xs inline-flex items-center gap-1"
                data-testid="post-add-media"
                disabled={files.length >= 4}
              >
                <ImageIcon className="h-4 w-4" /> {t("post.add_media", "Legg til bilde/video")}
              </button>
            </>
          )}

          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            className="input-editorial px-2 py-1 text-xs"
            data-testid="post-visibility"
          >
            {VIS_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>{t(o.labelKey, o.fallback)}</option>
            ))}
          </select>
          <Icon className="h-3.5 w-3.5 text-[var(--cream-faint)]" />

          {!isEdit && user?.is_creator && pool === "creator" && (
            <button
              onClick={() => setShowPromoModal(true)}
              type="button"
              className={`inline-flex items-center gap-1 text-xs font-meta px-2 py-1 border rounded ${
                promo
                  ? "border-[var(--magenta)] bg-[var(--magenta)]/10 text-[var(--magenta)]"
                  : "border-white/10 text-[var(--cream-soft)] hover:text-[var(--cream)]"
              }`}
              data-testid="post-promo-btn"
              title={promo ? t("post.promo_edit", "Rediger tilbud") : t("post.promo_add_hint", "Legg til tilbud i posten")}
            >
              <Sparkles className="h-3.5 w-3.5" />
              {promo ? `${t("post.promo_label", "Tilbud")}: ${promo.kind === "percent" ? `${promo.discount_pct}%` : promo.kind}` : t("post.promo_add", "Lag tilbud")}
            </button>
          )}

          <button
            onClick={() => setCommentsDisabled((v) => !v)}
            type="button"
            className={`inline-flex items-center gap-1 text-xs font-meta px-2 py-1 border rounded ${
              commentsDisabled
                ? "border-red-500/40 text-red-300"
                : "border-white/10 text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid="post-comments-toggle"
            title={commentsDisabled ? t("post.comments_off_tt", "Kommentarer er slått av") : t("post.comments_on_tt", "Kommentarer er på")}
          >
            {commentsDisabled
              ? <><MessageSquareOff className="h-3.5 w-3.5" /> {t("post.comments_off", "Kommentarer av")}</>
              : <><MessageSquare className="h-3.5 w-3.5" /> {t("post.comments_on", "Kommentarer på")}</>
            }
          </button>
          {!isEdit && visibility === "public" && !promo && files.length > 0 && (
            <label className="inline-flex items-center gap-1 text-xs font-meta text-[var(--cream-soft)] cursor-pointer" data-testid="post-optout-contest">
              <input type="checkbox" checked={optOutContest} onChange={(e) => setOptOutContest(e.target.checked)} data-testid="post-optout-contest-checkbox" />
              Ikke delta i konkurranser
            </label>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isEdit && (
            <button
              onClick={onCancel}
              disabled={busy}
              className="btn-ghost text-xs px-3 py-1.5"
              data-testid="post-cancel-edit"
            >
              {t("common.cancel", "Avbryt")}
            </button>
          )}
          <button
            onClick={submit}
            disabled={busy || (!isEdit && !text.trim() && files.length === 0 && !promo)}
            className="c-btn c-btn-primary text-sm px-4 py-1.5"
            data-testid="post-submit"
          >
            {busy
              ? (progress || t("post.publishing", "Publiserer…"))
              : isEdit
                ? t("post.save", "Lagre")
                : t("post.publish", "Publiser")}
          </button>
        </div>
      </div>

      <PromoModal
        open={showPromoModal}
        onClose={() => setShowPromoModal(false)}
        onApply={(data) => { setPromo(data); setShowPromoModal(false); }}
        initial={promo}
      />
    </div>
  );
}
